import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { EmailValidator, DomainValidator } from 'commons-validator-js';

export interface ExactLengthError {
  exactlength: number;
  actual: number;
}

export class NomidioValidators {
  // Practical max URL length accepted by most modern browsers
  protected static readonly MAXIMUM_URL_LENGTH = 2000;

  // Comprehensive set of characters available according to IETF RFC 3986: https://www.ietf.org/rfc/rfc3986.txt
  //
  // pct-encoded = "%" HEXDIG HEXDIG
  //
  // gen-delims  = ":" / "/" / "?" / "#" / "[" / "]" / "@"
  //
  // sub-delims  = "!" / "$" / "&" / "'" / "(" / ")"
  //             / "*" / "+" / "," / ";" / "="
  //
  // unreserved  = ALPHA / DIGIT / "-" / "." / "_" / "~"
  protected static readonly URL_REGEX = /^(http|ftp)(s?):\/\/[a-zA-Z0-9%._~!$&'()*+,;=:\\/?#[\]-]+$/;
  protected static readonly UUID_REGEX = /^[0-9a-zA-Z]{8}-[0-9a-zA-Z]{4}-[0-9a-zA-Z]{4}-[0-9a-zA-Z]{4}-[0-9a-zA-Z]{12}$/;

  protected static readonly emailValidator = new EmailValidator();
  protected static readonly domainValidator = new DomainValidator();

  /**
   * Validator that requires a field be an exact length.
   *
   * @returns A validator that returns `{ exactlength: { actual: number, exactlength: number } }` if the exact length is not met, or `null` otherwise.
   */
  public static exactLength(length: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string | undefined;

      if (typeof value?.length === 'undefined') {
        return null;
      }

      if (value.length !== length) {
        const feedback: ExactLengthError = { actual: value.length, exactlength: length };
        return { exactlength: feedback };
      }

      return null;
    };
  }

  /**
   * Validator that forbids a field from having certain values.
   *
   * @example
   * const email = new FormControl('', [ Validators.forbidden(['previous-email@example.com']) ]);
   * const newPassword = new FormControl('', [ Validators.forbidden(['current-password']) ]);
   *
   * @param forbiddenValues The list of forbidden values.
   * @returns A validator that returns `{ forbidden: true }` if the value is forbidden, or `null` otherwise.
   */
  public static forbidden<T>(forbiddenValues: T[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (forbiddenValues?.includes(control.value)) {
        return { forbidden: true };
      }
      return null;
    };
  }

  /**
   * Validator that requires a non-whitespace value be provided.
   *
   * @returns A validator that returns `{ required: true }` if the value is not provided, or `{ whitespace: true }` if it is just whitespace.
   */
  public static get requiredNonWhitespace(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string | undefined;

      if (typeof value === 'undefined' || value === null || !value.toString()) {
        return { required: true };
      } else if (/^\s+$/.test(value)) {
        return { whitespace: true };
      }

      return null;
    };
  }

  /**
   * Validator that requires the control doesn't contain a string that is just whitespace.
   *
   * @example '' — No errors.
   * @example 'foo' — No errrors.
   * @example ' foo ' — No errors.
   * @example '   ' — Error
   *
   * @returns A validator that returns `{ whitespace: true }` if the value is just whitespace.
   */
  public static get notJustWhitespace(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) {
        return null;
      }
      if (value.match(/^\s+$/)) {
        return { whitespace: true };
      }
      return null;
    };
  }

  /**
   * Validator that requires the control's value does not start or end with whitespace.
   *
   * @returns A validator that returns `{ whitespace: true }` if the value begins or ends with whitespace, or null otherwise.
   */
  public static get noWhitespaceWrap(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) {
        return null;
      }
      if (value.match(/^\s/) || value.match(/\s$/)) {
        return { whitespace: true };
      }
      return null;
    };
  }

  /**
   * Validator that requires the control's value is a valid Nomidio enterprise name.
   *
   * An enterprise name can contain Latin characters (and variations like diacritics), some special characters, and whitespace.
   * However, it cannot begin or end with whitespace.
   *
   * @returns A validator that returns `{ enterprisename: true }` (lowercase) if the value isn't a valid enterprise name, or `null` otherwise.
   */
  public static get enterpriseName(): ValidatorFn {
    const validEnterpriseName = /^([\wÀ-ÖØ-öø-ÿÆæǢǣǼǽ!?&.,:@`'#£*\s-])+$/;

    return (control: AbstractControl): ValidationErrors | null => {
      if (validEnterpriseName.test(control.value)) {
        return null;
      } else {
        return { enterprisename: true };
      }
    };
  }

  /**
   * Validator that requires the control's value is a valid email.
   *
   * This validator is more strict than the default Angular implementation found at {@link Validators.email}, and corresponds to back-end requirements.
   *
   * @returns A validator that returns `{ email: true }` if the email is invalid, or `null` if there's no issues
   */
  public static get email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || NomidioValidators.emailValidator.isValid(control.value)) {
        return null;
      } else {
        return { email: true };
      }
    };
  }

  /**
   * Validator that requries the control's value is a valid web domain.
   *
   * This is according to RFC1034 section 3, and RFC1123 section 2.1.
   *
   * @returns A validator that returns `{ domain: true }` if the domain is invalid, or `null` if there's no issues.
   */
  public static get domain(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || NomidioValidators.domainValidator.isValid(control.value)) {
        return null;
      } else {
        return { domain: true };
      }
    };
  }

  public static get uuid(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || this.UUID_REGEX.test(control.value)) {
        return null;
      } else {
        return { uuid: true };
      }
    };
  }

  /**
   * Validator that requires two controls in this group have matching values.
   *
   * @see NomidioValidators.noMatch for a requirement they *do not* match.
   *
   * @param referenceFieldName The field that has the value that should be matched.
   * @param matchingFieldName The field whose value should match the reference field.
   *
   * @returns A validator that returns `{ match: true }` if the values don't match, as well as set it on the matching (second) field.
   */
  public static match(referenceFieldName: string, matchingFieldName: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const reference = group.get(referenceFieldName);
      const matching = group.get(matchingFieldName);

      if (reference?.value === matching?.value) {
        return null;
      } else {
        matching?.setErrors({ match: true });
        return { match: true };
      }
    };
  }

  /**
   * Validator that requires this control *does not* match another control's value.
   *
   * @see NomidioValidators.match for a requirement they *do* match.
   *
   * @param referenceFieldName The field that has the value that should be matched.
   * @param matchingFieldName The field whose value *should not* match the reference field.
   *
   * @returns A validator that returns `{ nomatch: true }` if the values don't match, as well as set it on the matching (second) field.
   */
  public static noMatch(referenceFieldName: string, matchingFieldName: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const reference = group.get(referenceFieldName);
      const matching = group.get(matchingFieldName);

      if (reference?.value !== matching?.value) {
        return null;
      } else {
        matching?.setErrors({ nomatch: true });
        return { nomatch: true };
      }
    };
  }

  /**
   * Validator that requires the control's value is a valid URL.
   *
   * @returns A validator that returns `{ url: true }` if the url is invalid, `{ maxlength: true }` if it's too long, or `null` if there's no issues.
   */
  public static get url(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.toString?.() as string;
      const error = { url: true };

      if (!value) {
        return null;
      }

      if (value.length > NomidioValidators.MAXIMUM_URL_LENGTH) {
        return error;
      }

      try {
        // We use the url constructor to validate a url, it doesn't need to be used as a URL object
        // tslint:disable-next-line:no-unused-expression
        new URL(control.value);
      } catch {
        return error;
      }

      // Catch remaining pattern requirements
      if (!value.match(NomidioValidators.URL_REGEX)) {
        return error;
      }

      return null;
    };
  }
}
